import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const MEMORY_SIZE = 0x4000
const CHANNEL_COUNT = 256
const NAME_START = 0x1000
const NARROW_MASK = 0x02
const AM_MASK = 0x01
const SCAN_MASK = 0x20
const TX_MASK = 0x40
const RANGES = [
  [0x0000, 0x2400],
  [0x3400, 0x3c40],
  [0x3f80, 0x4000],
] as const

type RT490Spec = {
  id: string
  vendor: string
  model: string
  summary: string
}

export type RT490Profile = {
  protocol: 'rt490'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 256
  nameLength: 12
  memorySize: number
  magic: readonly number[]
  ranges: readonly (readonly [number, number])[]
  powerLevels: readonly ['High', 'Low']
}

const MAGIC = Array.from('PROGROMJJCCU', (character) =>
  character.charCodeAt(0),
)

const SPECS: readonly RT490Spec[] = [
  {
    id: 'radtel-rt-490',
    vendor: 'Radtel',
    model: 'RT-490',
    summary: 'Radtel RT-490 256-channel wideband handheld.',
  },
  {
    id: 'mmlradio-jc-8629',
    vendor: 'MMLradio',
    model: 'JC-8629',
    summary: 'MMLradio JC-8629 wideband handheld.',
  },
  {
    id: 'jjcc-jc-8629',
    vendor: 'JJCC',
    model: 'JC-8629',
    summary: 'JJCC JC-8629 wideband handheld.',
  },
  {
    id: 'socotran-jc-8629',
    vendor: 'Socotran',
    model: 'JC-8629',
    summary: 'Socotran JC-8629 wideband handheld.',
  },
  {
    id: 'socotran-fb-8629',
    vendor: 'Socotran',
    model: 'FB-8629',
    summary: 'Socotran FB-8629 wideband handheld.',
  },
  {
    id: 'jianpai-8800-plus',
    vendor: 'Jianpai',
    model: '8800_Plus',
    summary: 'Jianpai 8800 Plus wideband handheld.',
  },
  {
    id: 'boristone-8rs',
    vendor: 'Boristone',
    model: '8RS',
    summary: 'Boristone 8RS wideband handheld.',
  },
  {
    id: 'abbree-ar-869',
    vendor: 'Abbree',
    model: 'AR-869',
    summary: 'Abbree AR-869 wideband handheld.',
  },
  {
    id: 'hamgeek-hg-590',
    vendor: 'HamGeek',
    model: 'HG-590',
    summary: 'HamGeek HG-590 wideband handheld.',
  },
]

export const RT490_PROFILES: readonly RT490Profile[] = SPECS.map((spec) => ({
  protocol: 'rt490',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '256 channels', '12-character names'],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 12,
  memorySize: MEMORY_SIZE,
  magic: MAGIC,
  ranges: RANGES,
  powerLevels: ['High', 'Low'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value < 670) {
    const encoded = value - 1
    const index = encoded % DTCS_CODES.length
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[index] ?? null,
      polarity: index === encoded ? 'N' : 'R',
    }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? DTCS_CODES.length : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  let end = raw.findIndex((value) => value === 0xff || value === 0)
  if (end < 0) end = raw.length
  try {
    return new TextDecoder('gbk').decode(raw.slice(0, end)).trim()
  } catch {
    return String.fromCharCode(...raw.slice(0, end)).trim()
  }
}

export function parseRT490Channel(
  image: Uint8Array,
  profile: RT490Profile,
  number: number,
): WebChannel {
  const index = number - 1
  const offset = index * 16
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) return emptyChannel(number)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency =
    (raw[15] & TX_MASK) === 0 || raw[4] === 0xff
      ? null
      : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const nameOffset = NAME_START + index * 16
  const am = (raw[15] & AM_MASK) !== 0
  const narrow = (raw[15] & NARROW_MASK) !== 0
  return {
    number,
    empty: false,
    name: decodeName(image.slice(nameOffset, nameOffset + 12)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: am ? 'AM' : narrow ? 'NFM' : 'FM',
    power: raw[14] >> 4 === 1 ? 'Low' : 'High',
    skip: (raw[15] & SCAN_MASK) === 0,
  }
}

export function parseRT490Channels(
  image: Uint8Array,
  profile: RT490Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, index) => parseRT490Channel(image, profile, index + 1),
  )
}

export function applyRT490Channel(
  image: Uint8Array,
  profile: RT490Profile,
  channel: WebChannel,
): void {
  if (
    channel.number < profile.channelMinimum ||
    channel.number > profile.channelMaximum
  ) {
    throw new Error('Channel must be between 1 and 256.')
  }
  const index = channel.number - 1
  const offset = index * 16
  const raw = image.subarray(offset, offset + 16)
  const nameOffset = NAME_START + index * 16
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameOffset, nameOffset + 16)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    encodeLbcd(channel.rxFrequency, raw.subarray(4, 8))
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] = (raw[14] & 0x0f) | (channel.power === 'Low' ? 0x10 : 0)
  raw[15] =
    (raw[15] & ~(AM_MASK | NARROW_MASK | SCAN_MASK | TX_MASK)) |
    (channel.mode === 'AM' ? AM_MASK : 0) |
    (channel.mode === 'NFM' || channel.mode === 'AM' ? NARROW_MASK : 0) |
    (channel.skip ? 0 : SCAN_MASK) |
    (channel.txFrequency ? TX_MASK : 0)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 12)
    .padEnd(12, '\u00ff')
  image.fill(0xff, nameOffset, nameOffset + 16)
  for (let position = 0; position < name.length; position += 1) {
    image[nameOffset + position] = name.charCodeAt(position) & 0xff
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  transport: RadioTransport,
): Promise<void> {
  let lastError: unknown = null
  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      await transport.discardInput()
      await transport.write(Uint8Array.from(MAGIC))
      await readAck(transport, 'The radio refused RT-490 programming mode.')
      await transport.write(new TextEncoder().encode('F'))
      await transport.readExact(8, 3000)
      return
    } catch (cause) {
      lastError = cause
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('No supported RT-490 radio responded.')
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
  )
  const response = await transport.readExact(4 + BLOCK_SIZE, 4000)
  if (
    response[0] !== 0x52 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Unexpected RT-490 read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + BLOCK_SIZE)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(data, 4)
  await transport.write(frame)
  await readAck(
    transport,
    `The radio rejected RT-490 write block 0x${address.toString(16)}.`,
  )
}

export async function readRT490RadioWithTransport(
  profile: RT490Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading RT-490 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeRT490RadioWithTransport(
  profile: RT490Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: (end - start) / BLOCK_SIZE },
        (_, index) => start + index * BLOCK_SIZE,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing RT-490 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
